<template>
  <div class="tl">
    <Breadcrumbs main="Пользователи" title="Логи авторизации"/>

    <div class="container-fluid tl-container">

      <!-- ================== TOOLBAR ================== -->
      <div class="tl-toolbar">
        <div class="tl-toolbar__main">
          <div class="tl-search" :class="{ 'is-busy': isLoadingLogs }">
            <i class="fa fa-search tl-search__icon"></i>
            <input
                ref="search"
                type="search"
                class="tl-search__input"
                v-model="searchModel"
                placeholder="Email пользователя или IP-адрес…"
                @keydown.esc.prevent="clearSearch"
            />
            <button v-if="searchModel" type="button" class="tl-search__clear" @click="clearSearch">
              <i class="fa fa-times"></i>
            </button>
            <kbd v-else class="tl-search__kbd">/</kbd>
          </div>

          <span v-if="searchApplied" class="tl-badge-hint">
            Ищем по полю <b>{{ activeFilterLabel }}</b>
          </span>

          <div class="tl-spacer"></div>

          <button type="button" class="tl-icon-btn" :class="{ 'is-on': density === 'compact' }"
                  @click="toggleDensity" v-b-tooltip.hover.bottom="'Плотность строк'">
            <i class="fa fa-bars"></i>
          </button>

          <router-link :to="{ name: 'users_real' }" class="tl-icon-btn"
                       v-b-tooltip.hover.bottom="'Реальные пользователи'">
            <i class="fa fa-users"></i>
          </router-link>

          <button type="button" class="tl-icon-btn" :class="{ 'is-spin': isLoadingLogs }"
                  @click="refresh" v-b-tooltip.hover.bottom="'Обновить'">
            <i class="fa fa-refresh"></i>
          </button>
        </div>

        <div v-if="searchApplied" class="tl-chips">
          <span class="tl-chips__label">Фильтры:</span>
          <button type="button" class="tl-chip" @click="clearSearch">
            <i class="fa fa-search"></i> {{ activeFilterLabel }}: «{{ searchApplied }}»
            <i class="fa fa-times tl-chip__x"></i>
          </button>
          <button type="button" class="tl-chips__reset" @click="clearSearch">Сбросить всё</button>
        </div>
      </div>

      <!-- ================== CONTENT ================== -->
      <div class="tl-panel" :class="['tl-panel--' + density]">

        <div v-if="isLoadingLogs" class="tl-skeleton">
          <div v-for="n in 10" :key="'sk' + n" class="tl-skeleton__row">
            <span class="tl-sk tl-sk--avatar"></span>
            <span class="tl-sk tl-sk--wide"></span>
            <span class="tl-sk"></span>
            <span class="tl-sk"></span>
            <span class="tl-sk tl-sk--sm"></span>
          </div>
        </div>

        <div v-else-if="!rows.length" class="tl-empty">
          <div class="tl-empty__art"><i class="fa fa-sign-in"></i></div>
          <div class="tl-empty__title">
            {{ searchApplied ? 'Ничего не нашлось' : 'Логов нет' }}
          </div>
          <div class="tl-empty__text">
            {{ searchApplied
              ? 'Поиск работает по email пользователя или IPv4-адресу целиком.'
              : 'Здесь появятся записи о входах пользователей.' }}
          </div>
          <div v-if="searchApplied" class="tl-empty__actions">
            <button type="button" class="tl-btn-ghost-2" @click="clearSearch">Сбросить поиск</button>
          </div>
        </div>

        <div v-else class="tl-table-wrap">
          <b-table
              ref="table"
              class="tl-table"
              responsive
              hover
              borderless
              show-empty
              :items="rows"
              :fields="fields"
          >
            <template #cell(id)="{ item }">
              <span class="tl-id tl-id--sm">#{{ item.id }}</span>
            </template>

            <template #cell(user)="{ item }">
              <div class="tl-name">
                <span class="tl-name__logo">{{ initials(item.x_username) }}</span>
                <div class="tl-name__col">
                  <button v-if="item.x_userId" type="button" class="tl-name__title"
                          @click.stop="openUser(item.x_userId)" :title="item.x_username">
                    {{ item.x_username || '—' }}
                  </button>
                  <span v-else class="tl-name__title">{{ item.x_username || '—' }}</span>
                  <span class="tl-muted tl-mono">{{ item.x_email || '—' }}</span>
                </div>
              </div>
            </template>

            <template #cell(ip)="{ item }">
              <button type="button" class="tl-copy tl-mono" @click.stop="copyValue(item.ip, 'IP')"
                      v-b-tooltip.hover.top="'Скопировать IP'">
                {{ item.ip || '—' }} <i class="fa fa-copy"></i>
              </button>
            </template>

            <template #cell(country)="{ item }">
              <span class="tl-muted-strong">{{ item.x_country || '—' }}</span>
            </template>

            <template #cell(created_at)="{ item }">
              <div class="tl-time">
                <span class="tl-time__abs">{{ formatDateTime(item.created_at) }}</span>
                <span class="tl-time__rel">{{ formatRelative(item.created_at) }}</span>
              </div>
            </template>

            <template #cell(data)="{ item }">
              <div v-if="item.x_hasData" class="tl-json">
                <button type="button" class="tl-json__toggle" @click.stop="toggleData(item.id)">
                  <i class="fa" :class="expanded[item.id] ? 'fa-caret-down' : 'fa-caret-right'"></i>
                  {{ expanded[item.id] ? 'скрыть' : 'показать' }}
                </button>
                <pre v-if="expanded[item.id]" class="tl-json__body">{{ item.x_data }}</pre>
              </div>
              <span v-else class="tl-muted">—</span>
            </template>

            <template #empty>
              <div class="tl-empty tl-empty--inline">Ничего не найдено</div>
            </template>
          </b-table>
        </div>

        <div v-if="!isLoadingLogs && rows.length" class="tl-foot">
          <div class="tl-foot__info">
            Показано <b>{{ rangeFrom }}–{{ rangeTo }}</b> из <b>{{ totalLoginLogsPagination || 0 }}</b>
          </div>
          <b-pagination
              class="tl-pagination my-0"
              v-model="pageModel"
              :total-rows="totalLoginLogsPagination"
              :per-page="perPage"
              :limit="7"
              @change="goToPage"
          />
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import {UserLogsAdapterMixin} from "../../mixins/Logs/UserLogsAdapterMixin";
import {PaginationHelperMixin} from "@/mixins/Helpers/PaginationHelperMixin";

const LS = {density: "userLoginLogsDensity"};

// Всё, что не похоже на IPv4, бэк ищет по email пользователя
const IPV4_RE = /^(\d{1,3}\.){3}\d{1,3}$/;

export default {
  name: "UserLoginLogsInfo",
  mixins: [UserLogsAdapterMixin, PaginationHelperMixin],
  data() {
    return {
      pageKey: 'pageNumberUserLoginLogs',
      page: 1,

      searchModel: '',
      searchApplied: '',
      searchTimer: null,

      density: 'cozy',
      expanded: {},

      fields: [
        {key: 'id', label: 'ID'},
        {key: 'user', label: 'Пользователь'},
        {key: 'ip', label: 'IP-адрес'},
        {key: 'country', label: 'Страна'},
        {key: 'created_at', label: 'Дата авторизации'},
        {key: 'data', label: 'Доп. информация'},
      ],
    }
  },
  computed: {
    /** Размер страницы приходит с бэка (pagination.by), локально его не выдумываем. */
    perPage() {
      return Number(this.itemsPerPage) || 15;
    },

    pageModel: {
      get() {
        return Number(this.page) || 1;
      },
      set(value) {
        this.page = value;
      },
    },

    activeFilterLabel() {
      return this.filterField === 'ip' ? 'IP-адрес' : 'Email';
    },

    filterField() {
      if (IPV4_RE.test(this.searchApplied)) return 'ip';
      return 'user_email';
    },

    rows() {
      return (this.loginLogs || []).map(log => {
        const data = log.data;
        const hasData = data !== null && data !== undefined
            && !(typeof data === 'object' && !Object.keys(data).length);
        return {
          ...log,
          x_userId: (log.user && log.user.id) || null,
          x_username: (log.user && log.user.username) || '',
          x_email: (log.user && log.user.email) || '',
          x_country: (log.country && log.country.name) || '',
          x_hasData: hasData,
          x_data: hasData ? JSON.stringify(data, null, 2) : '',
        };
      });
    },

    rangeFrom() {
      if (!this.totalLoginLogsPagination) return 0;
      return (this.pageModel - 1) * this.perPage + 1;
    },

    rangeTo() {
      return Math.min(this.pageModel * this.perPage, this.totalLoginLogsPagination || 0);
    },
  },
  watch: {
    searchModel(value) {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        if (this.searchApplied === value.trim()) return;
        this.searchApplied = value.trim();
        this.page = 1;
        this.fetchLogs();
      }, 400);
    },
  },
  mounted() {
    this.restorePreferences();
    this.page = Number(this.getCurrentPageNumber(this.pageKey)) || 1;
    this.fetchLogs();
    document.addEventListener("keydown", this.onKeydown);
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onKeydown);
    if (this.searchTimer) clearTimeout(this.searchTimer);
  },
  methods: {
    fetchLogs() {
      this.recordPageToStorage(this.pageModel, this.pageKey);
      const params = {page: this.pageModel};
      if (this.searchApplied) {
        params[`filter[${this.filterField}]`] = this.searchApplied;
      }
      this.expanded = {};
      return this.loadLoginLogs(params);
    },

    refresh() {
      this.fetchLogs();
    },

    goToPage(pageNumber) {
      this.page = pageNumber;
      this.$nextTick(this.fetchLogs);
    },

    clearSearch() {
      this.searchModel = '';
      if (!this.searchApplied) return;
      this.searchApplied = '';
      this.page = 1;
      this.fetchLogs();
    },

    toggleData(id) {
      this.$set(this.expanded, id, !this.expanded[id]);
    },

    openUser(id) {
      const route = this.$router.resolve({name: 'user_info', params: {id}});
      window.open(route.href, '_blank');
    },

    copyValue(value, label) {
      if (!value) return;
      const text = String(value);
      const done = () => this.$toasted.success(`${label} скопирован`, {position: 'top-right', duration: 1500});
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done).catch(() => {
        });
        return;
      }
      const area = document.createElement("textarea");
      area.value = text;
      document.body.appendChild(area);
      area.select();
      try {
        document.execCommand("copy");
        done();
      } finally {
        document.body.removeChild(area);
      }
    },

    initials(name) {
      if (!name) return "?";
      return String(name).trim().slice(0, 2).toUpperCase();
    },

    formatDateTime(value) {
      if (!value) return "—";
      const date = new Date(String(value).replace(" ", "T"));
      if (isNaN(date.getTime())) return "—";
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    },

    formatRelative(value) {
      if (!value) return "";
      const date = new Date(String(value).replace(" ", "T"));
      if (isNaN(date.getTime())) return "";
      const minutes = Math.round((Date.now() - date.getTime()) / 60000);
      if (minutes < 1) return "только что";
      if (minutes < 60) return `${minutes} мин. назад`;
      const hours = Math.round(minutes / 60);
      if (hours < 24) return `${hours} ч. назад`;
      const days = Math.round(hours / 24);
      if (days < 30) return `${days} дн. назад`;
      return `${Math.round(days / 30)} мес. назад`;
    },

    restorePreferences() {
      try {
        const density = localStorage.getItem(LS.density);
        if (density === "compact" || density === "cozy") this.density = density;
      } catch (e) { /* повреждённые настройки — берём значения по умолчанию */
      }
    },

    toggleDensity() {
      this.density = this.density === "compact" ? "cozy" : "compact";
      localStorage.setItem(LS.density, this.density);
    },

    onKeydown(event) {
      const tag = (event.target && event.target.tagName) || "";
      const isTyping = /INPUT|TEXTAREA|SELECT/.test(tag) || (event.target && event.target.isContentEditable);

      if (event.key === "Escape") {
        if (isTyping) return;
        if (this.searchApplied) this.clearSearch();
        return;
      }

      if (isTyping || event.ctrlKey || event.metaKey || event.altKey) return;

      if (event.key === "/") {
        event.preventDefault();
        if (this.$refs.search) this.$refs.search.focus();
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import "~@/assets/scss/admin-list.scss";

.tl-name__col {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.tl-name__title {
  border: 0;
  background: none;
  padding: 0;
  text-align: left;
  cursor: pointer;
}

.tl-name__logo {
  font-weight: 700;
  font-size: 12px;
}

.tl-badge-hint {
  font-size: 11.5px;
  color: var(--tl-muted);

  b { color: var(--tl-text); }
}

.tl-copy {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 0;
  background: none;
  padding: 0;
  color: var(--tl-text);
  cursor: pointer;

  i { opacity: 0; font-size: 10px; transition: opacity .14s ease; }

  &:hover {
    color: var(--tl-primary);

    i { opacity: 1; }
  }
}

.tl-json {
  &__toggle {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    border: 0;
    background: none;
    padding: 0;
    color: var(--tl-primary);
    font-size: 11.5px;
    cursor: pointer;
  }

  &__body {
    margin: 6px 0 0;
    padding: 8px 10px;
    max-height: 200px;
    max-width: 420px;
    overflow: auto;
    border-radius: 8px;
    background: var(--tl-soft);
    color: var(--tl-text);
    font-size: 11px;
    line-height: 1.45;
  }
}
</style>
